import { motion } from 'framer-motion'
import { 
  ArrowDownLeft, 
  ArrowUpRight, 
  CreditCard, 
  Gift, 
  Wallet,
  RefreshCw
} from 'lucide-react'
import { Transaction } from '@/types'
import { formatCurrency, formatDate, cn } from '@/lib/utils'

interface TransactionItemProps {
  transaction: Transaction
  onClick?: () => void
}

const typeConfig: Record<string, { icon: typeof ArrowDownLeft; color: string; label: string }> = {
  deposit: { icon: ArrowDownLeft, color: 'text-accent-green', label: 'Deposit' },
  withdrawal: { icon: ArrowUpRight, color: 'text-accent-red', label: 'Withdrawal' },
  card_payment: { icon: CreditCard, color: 'text-text-primary', label: 'Payment' },
  transfer_in: { icon: ArrowDownLeft, color: 'text-accent-green', label: 'Transfer In' },
  transfer_out: { icon: ArrowUpRight, color: 'text-accent-red', label: 'Transfer Out' },
  reward: { icon: Gift, color: 'text-brand', label: 'Reward' },
  fee: { icon: RefreshCw, color: 'text-text-tertiary', label: 'Fee' },
}

export default function TransactionItem({ transaction, onClick }: TransactionItemProps) {
  const config = typeConfig[transaction.type] || typeConfig.deposit
  const Icon = config.icon
  const isPositive = transaction.amount > 0
  
  return (
    <motion.button
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-background-elevated transition-colors text-left"
    >
      {/* Icon */}
      <div className={cn(
        "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
        transaction.type === 'reward' ? 'bg-brand/20' : 'bg-background'
      )}>
        {transaction.merchantIcon ? (
          <span className="text-lg">{transaction.merchantIcon}</span>
        ) : (
          <Icon className={cn("w-5 h-5", config.color)} />
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-text-primary truncate">
          {transaction.merchantName || transaction.description || config.label}
        </p>
        <p className="text-sm text-text-tertiary">
          {formatDate(transaction.timestamp, { format: 'relative' })}
          {transaction.cardLastFour && ` • •••••${transaction.cardLastFour}`}
        </p>
      </div>
      
      {/* Amount */}
      <div className="text-right">
        <p className={cn(
          "font-semibold",
          isPositive ? 'text-accent-green' : 'text-text-primary'
        )}>
          {isPositive ? '+' : ''}{formatCurrency(Math.abs(transaction.amount))}
        </p>
        <p className="text-xs text-text-tertiary">
          {transaction.currency}
        </p>
      </div>
    </motion.button>
  )
}